import React, { useEffect, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import { Activity, Search, RefreshCw, Eye, Globe, ShieldAlert, AlertTriangle, Rocket, Server, Boxes, Clock, ExternalLink } from "lucide-react";
import api, { notifyError } from "@/lib/api";
import PlatformLayout from "@/components/PlatformLayout";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { toast } from "sonner";
import { HealthBadge, SeverityBadge, HealthScoreRing } from "./monitoring/MonitoringBadges";

const TRIGGER = "bg-white/5 border-white/10 text-white hover:bg-white/10";
const CONTENT = "z-[110] bg-[#0F0F17] border-white/10 text-white";
const ITEM = "text-white focus:bg-white/10 focus:text-white";

function fmt(iso) {
  if (!iso) return "—";
  try { return new Date(iso).toLocaleString("id-ID", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" }); }
  catch { return iso; }
}

function SummaryCard({ label, value, tone = "neutral", icon: Icon, testid }) {
  const tones = {
    neutral: "text-white/70", healthy: "text-emerald-300", warning: "text-amber-300",
    critical: "text-rose-300", info: "text-sky-300", ssl: "text-cyan-300",
  };
  return (
    <div className="rounded-2xl border border-white/10 bg-white/5 p-4" data-testid={testid}>
      <div className="flex items-center gap-2 mb-1.5">
        {Icon ? <Icon size={14} className={tones[tone]} /> : null}
        <div className="text-[10px] uppercase tracking-widest text-white/40 font-bold">{label}</div>
      </div>
      <div className={`text-2xl font-black font-display ${tones[tone]}`}>{value}</div>
    </div>
  );
}

export default function PlatformMonitoring() {
  const [items, setItems] = useState([]);
  const [summary, setSummary] = useState(null);
  const [alerts, setAlerts] = useState([]);
  const [infra, setInfra] = useState(null);
  const [q, setQ] = useState("");
  const [status, setStatus] = useState("");
  const [loading, setLoading] = useState(true);
  const [refreshingAll, setRefreshingAll] = useState(false);
  const [refreshingId, setRefreshingId] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (q) params.q = q;
      if (status) params.status = status;
      const [m, i] = await Promise.all([
        api.get("/platform/monitoring", { params }),
        api.get("/platform/monitoring/infrastructure"),
      ]);
      setItems(m.data.items || []);
      setSummary(m.data.summary || null);
      setAlerts(m.data.alerts || []);
      setInfra(i.data || null);
    } catch (e) { notifyError(e); } finally { setLoading(false); }
  }, [q, status]);

  useEffect(() => { load(); }, [load]);

  const refreshAll = async () => {
    setRefreshingAll(true);
    try {
      const r = await api.post("/platform/monitoring/refresh", {});
      toast.success(`Snapshot dibuat untuk ${r.data.website_snapshots_count} website.`);
      load();
    } catch (e) { notifyError(e, "Refresh gagal"); }
    finally { setRefreshingAll(false); }
  };

  const refreshOne = async (id) => {
    setRefreshingId(id);
    try {
      await api.post(`/platform/monitoring/refresh/${id}`, {});
      toast.success("Snapshot tenant tersimpan.");
      load();
    } catch (e) { notifyError(e, "Refresh gagal"); }
    finally { setRefreshingId(null); }
  };

  const platformStatus = summary?.platform_status || "UNKNOWN";
  const platformScore = summary?.platform_score ?? 0;

  return (
    <PlatformLayout title="Monitoring">
      <div data-testid="platform-monitoring-page">
        {/* Header + Refresh All */}
        <div className="flex items-center gap-3 flex-wrap mb-5">
          <HealthBadge status={platformStatus} />
          <div className="text-white/60 text-xs">Platform Status</div>
          <div className="ml-auto flex items-center gap-2">
            <Button variant="ghost" onClick={refreshAll} disabled={refreshingAll} className="text-white/80 hover:text-white hover:bg-white/10 border border-white/10" data-testid="mon-refresh-all">
              <RefreshCw size={14} className={`mr-2 ${refreshingAll ? "animate-spin" : ""}`} />
              {refreshingAll ? "Menyimpan snapshot..." : "Refresh All (Snapshot)"}
            </Button>
          </div>
        </div>

        {/* Health Overview */}
        <div className="rounded-2xl border border-white/10 bg-white/5 p-5 mb-5 flex items-center gap-6" data-testid="mon-health-overview">
          <HealthScoreRing value={platformScore} size={120} stroke={12} testid="mon-platform-ring" />
          <div className="flex-1 min-w-0 space-y-1">
            <div className="text-[10px] uppercase tracking-widest text-white/40 font-bold">Platform Health Score</div>
            <div className="text-3xl font-black font-display text-white">
              {platformScore} / 100
            </div>
            <div className="text-xs text-white/50">
              {platformScore >= 90 ? "Semua sistem sehat." : platformScore >= 70 ? "Ada beberapa peringatan yang perlu ditinjau." : "Ada masalah kritikal yang perlu perhatian segera."}
            </div>
            <div className="text-[10px] text-white/40 pt-1">Terakhir diperiksa: {fmt(summary?.last_checked_at)}</div>
          </div>
        </div>

        {/* Summary Cards */}
        <div className="grid grid-cols-2 md:grid-cols-6 gap-3 mb-5">
          <SummaryCard label="Total" value={summary?.total_websites ?? 0} icon={Globe} testid="mon-summary-total" />
          <SummaryCard label="Healthy" value={summary?.healthy_websites ?? 0} tone="healthy" icon={Activity} testid="mon-summary-healthy" />
          <SummaryCard label="Warning" value={summary?.warning_websites ?? 0} tone="warning" icon={AlertTriangle} testid="mon-summary-warning" />
          <SummaryCard label="Critical" value={summary?.critical_websites ?? 0} tone="critical" icon={ShieldAlert} testid="mon-summary-critical" />
          <SummaryCard label="Offline" value={summary?.offline_websites ?? 0} tone="critical" icon={Globe} testid="mon-summary-offline" />
          <SummaryCard label="Avg RT (s)" value={summary?.avg_response_time != null ? summary.avg_response_time : "—"} tone="info" icon={Clock} testid="mon-summary-avg-rt" />
        </div>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-5">
          <SummaryCard label="SSL Expiring" value={summary?.ssl_expiring_count ?? 0} tone="warning" icon={ShieldAlert} testid="mon-summary-ssl-expiring" />
          <SummaryCard label="SSL Expired" value={summary?.ssl_expired_count ?? 0} tone="critical" icon={ShieldAlert} testid="mon-summary-ssl-expired" />
          <SummaryCard label="Deployment Failed" value={summary?.deployment_failed_count ?? 0} tone="critical" icon={Rocket} testid="mon-summary-deploy-failed" />
          <SummaryCard label="Infra Failed" value={summary?.infrastructure_failed_count ?? 0} tone="critical" icon={Server} testid="mon-summary-infra-failed" />
        </div>

        {/* Alerts Panel */}
        <div className="rounded-2xl border border-white/10 bg-white/5 p-4 mb-5" data-testid="mon-alerts-panel">
          <div className="flex items-center gap-2 mb-3">
            <AlertTriangle size={14} className="text-amber-300" />
            <div className="text-[11px] uppercase tracking-widest text-white/40 font-bold">Alerts</div>
            <div className="ml-auto text-[10px] text-white/50">{alerts.length} aktif</div>
          </div>
          {alerts.length === 0 ? (
            <div className="text-emerald-300/70 text-xs italic py-2" data-testid="mon-alerts-empty">Tidak ada alert. Semua sistem baik-baik saja.</div>
          ) : (
            <div className="space-y-2 max-h-64 overflow-y-auto pr-1">
              {alerts.slice(0, 25).map((a, i) => (
                <div key={`${a.type}-${a.tenant_id || i}-${i}`} className="flex items-center gap-3 rounded-lg border border-white/5 bg-black/30 p-2.5" data-testid={`mon-alert-${i}`}>
                  <SeverityBadge severity={a.severity} />
                  <div className="flex-1 min-w-0">
                    <div className="text-xs font-bold text-white truncate">{a.type.replaceAll("_", " ")}</div>
                    <div className="text-[10px] text-white/60 truncate">{a.message} {a.tenant_name ? `· ${a.tenant_name}` : ""}</div>
                  </div>
                  {a.tenant_id && (
                    <Link to={`/platform/monitoring/${a.tenant_id}`} className="text-white/50 hover:text-white text-xs" data-testid={`mon-alert-open-${i}`}>
                      <ExternalLink size={12} />
                    </Link>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Filters */}
        <div className="mb-4 flex items-center gap-2 flex-wrap">
          <div className="flex-1 min-w-[200px] relative">
            <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-white/40" />
            <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Cari tenant / domain / slug…"
              className="bg-white/5 border-white/10 text-white placeholder:text-white/30 pl-9" data-testid="mon-search" />
          </div>
          <Select value={status || "ALL"} onValueChange={(v) => setStatus(v === "ALL" ? "" : v)}>
            <SelectTrigger className={`w-40 ${TRIGGER}`} data-testid="mon-filter-status"><SelectValue placeholder="Status" /></SelectTrigger>
            <SelectContent className={CONTENT}>
              {["ALL", "HEALTHY", "WARNING", "CRITICAL", "UNKNOWN"].map((v) => (
                <SelectItem key={v} value={v} className={ITEM}>{v}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Button variant="ghost" onClick={load} className="text-white/60 hover:text-white hover:bg-white/10" data-testid="mon-reload">
            <RefreshCw size={14} className="mr-1" /> Reload
          </Button>
        </div>

        {/* Website Monitoring Table */}
        {loading ? (
          <div className="text-white/40 text-sm p-6 rounded-2xl border border-white/10 text-center" data-testid="mon-table-loading">Memuat…</div>
        ) : items.length === 0 ? (
          <div className="text-white/40 text-sm p-8 rounded-2xl border border-white/10 text-center" data-testid="mon-table-empty">Tidak ada tenant / tidak ada hasil filter.</div>
        ) : (
          <div className="rounded-2xl border border-white/10 overflow-hidden mb-5" data-testid="mon-table">
            <div className="hidden md:grid grid-cols-12 gap-2 px-4 py-3 bg-white/5 text-[10px] uppercase tracking-wider text-white/40 font-bold">
              <div className="col-span-3">Website / Tenant</div>
              <div className="col-span-2">Domain</div>
              <div className="col-span-1">Status</div>
              <div className="col-span-1">Score</div>
              <div className="col-span-1">HTTP</div>
              <div className="col-span-1">RT (s)</div>
              <div className="col-span-1">SSL</div>
              <div className="col-span-1">Last Deploy</div>
              <div className="col-span-1 text-right">Actions</div>
            </div>
            {items.map((w) => (
              <div key={w.tenant_id} className="grid grid-cols-1 md:grid-cols-12 gap-2 px-4 py-3 border-t border-white/5 items-center"
                data-testid={`mon-row-${w.tenant_id}`}>
                <div className="col-span-3 min-w-0">
                  <Link to={`/platform/monitoring/${w.tenant_id}`} className="text-sm font-bold text-white hover:text-emerald-300 truncate block" data-testid={`mon-name-${w.tenant_id}`}>
                    {w.tenant_name}
                  </Link>
                  <div className="text-[10px] text-white/40 font-mono">{w.tenant_code} · {w.tenant_slug}</div>
                </div>
                <div className="col-span-2 text-xs font-mono text-white truncate">{w.domain || "—"}</div>
                <div className="col-span-1"><HealthBadge status={w.status} size="sm" /></div>
                <div className="col-span-1 text-sm font-black font-display text-white">{w.health_score}</div>
                <div className="col-span-1 text-xs font-mono text-white/70">{w.http_status ?? "—"}</div>
                <div className="col-span-1 text-xs font-mono text-white/70">{w.response_time ?? "—"}</div>
                <div className="col-span-1 text-[10px] font-mono text-white/60 truncate">{w.ssl_status}</div>
                <div className="col-span-1 text-[10px] text-white/40">{fmt(w.last_deployment)}</div>
                <div className="col-span-1 flex gap-1 justify-end">
                  <button onClick={() => refreshOne(w.tenant_id)} disabled={refreshingId === w.tenant_id}
                    className="w-7 h-7 rounded-lg grid place-items-center text-white/60 hover:text-white hover:bg-white/10 disabled:opacity-50"
                    title="Refresh" data-testid={`mon-refresh-${w.tenant_id}`}>
                    <RefreshCw size={12} className={refreshingId === w.tenant_id ? "animate-spin" : ""} />
                  </button>
                  <Link to={`/platform/monitoring/${w.tenant_id}`} className="w-7 h-7 rounded-lg grid place-items-center text-white/60 hover:text-white hover:bg-white/10"
                    title="Detail" data-testid={`mon-view-${w.tenant_id}`}>
                    <Eye size={12} />
                  </Link>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Infrastructure Panel */}
        <div className="rounded-2xl border border-white/10 bg-white/5 p-4" data-testid="mon-infra-panel">
          <div className="flex items-center gap-2 mb-3">
            <Server size={14} className="text-indigo-300" />
            <div className="text-[11px] uppercase tracking-widest text-white/40 font-bold">Infrastructure Overview</div>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-4">
            <SummaryCard label="Total Executions" value={infra?.total_executions ?? 0} icon={Server} testid="mon-infra-total" />
            <SummaryCard label="Total Containers" value={infra?.total_containers ?? 0} icon={Boxes} testid="mon-infra-containers" />
            <SummaryCard label="Running Containers" value={infra?.running_containers ?? 0} tone="healthy" icon={Boxes} testid="mon-infra-running" />
            <SummaryCard label="Healthy Executions" value={infra?.healthy_executions ?? 0} tone="healthy" icon={Activity} testid="mon-infra-healthy" />
          </div>
          <div className="grid md:grid-cols-2 gap-4">
            <div>
              <div className="text-[10px] uppercase tracking-widest text-white/40 font-bold mb-2">Latest Executions</div>
              {(infra?.latest_executions || []).slice(0, 5).length === 0 ? (
                <div className="text-white/40 text-xs italic">Belum ada.</div>
              ) : (infra?.latest_executions || []).slice(0, 5).map((e) => (
                <div key={e.id} className="flex items-center gap-2 text-xs py-1.5 border-b border-white/5 last:border-0" data-testid={`mon-infra-exec-${e.id}`}>
                  <span className="font-mono text-white truncate">{e.execution_number}</span>
                  <span className="ml-auto font-mono text-[10px] text-white/60">{e.status}</span>
                  <span className="text-[10px] text-white/40 font-mono">{e.adapter}</span>
                </div>
              ))}
            </div>
            <div>
              <div className="text-[10px] uppercase tracking-widest text-white/40 font-bold mb-2">Latest Deployments</div>
              {(infra?.latest_deployments || []).slice(0, 5).length === 0 ? (
                <div className="text-white/40 text-xs italic">Belum ada.</div>
              ) : (infra?.latest_deployments || []).slice(0, 5).map((d) => (
                <div key={d.id} className="flex items-center gap-2 text-xs py-1.5 border-b border-white/5 last:border-0" data-testid={`mon-infra-dep-${d.id}`}>
                  <span className="font-mono text-white truncate">{d.deployment_number}</span>
                  <span className="ml-auto font-mono text-[10px] text-white/60">{d.status}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </PlatformLayout>
  );
}
